import { useEffect, useState } from 'react';

import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  makeStyles,
  TextField,
} from '@material-ui/core';
import { ExpandMore } from '@material-ui/icons';
import { Autocomplete } from '@material-ui/lab';

import AutocompleteAtendimentoReason from 'components/AutocompleteAtendimentoReason';
import DialogHeader from 'components/DialogHeader';
import { TagItem } from 'components/TagsV2/TagItem';
import { useAppDispatch, useAppSelector } from 'store';
import {
  AtendimentoReasonInterface,
  atendimentosSelector,
  fetchFinishReasonSetting,
} from 'store/reducers/atendimentos';
import { fetchTags, TagInterface, tagsSelector } from 'store/reducers/tags';

const useStyles = makeStyles(({ palette, spacing }) => ({
  root: {
    '& .MuiDialog-paper': {
      borderRadius: 8,
    },
  },
  content: {
    paddingTop: `${spacing(1)}px !important`,
    paddingBottom: `${spacing(1)}px !important`,
    '& > *': {
      marginBottom: `${spacing(2)}px !important`,
    },
    '& > *:last-child': {
      marginBottom: '0 !important',
    },
  },
  cancelButton: {
    fontWeight: 500,
    textTransform: 'none',
    color: palette.text.primary,
  },
  confirmButton: {
    fontWeight: 500,
    textTransform: 'none',
    backgroundColor: palette.secondary.main,
    color: palette.common.white,
    '&:hover': {
      backgroundColor: palette.secondary.dark,
    },
  },
  listbox: {
    scrollbarGutter: 'stable',
  },
  option: {
    fontSize: spacing(1.5),
    cursor: 'pointer',
    paddingRight: 0,
    paddingLeft: spacing(1),
    borderRadius: spacing(0.5),
    marginLeft: spacing(1),
  },
  row: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    width: '100%',
    marginRight: 8,
  },
}));

interface ModalFinalizarAtendimentoProps {
  open: boolean;
  onClose: () => void;
  onConfirm: (data: {
    atendimentoReasonId?: string;
    tagIds?: string[];
  }) => void;
  contactName: string;
  loading?: boolean;
}

export default function ModalFinalizarAtendimento({
  open,
  onClose,
  onConfirm,
  contactName,
  loading = false,
}: ModalFinalizarAtendimentoProps) {
  const classes = useStyles();
  const dispatch = useAppDispatch();
  const { tags } = useAppSelector(tagsSelector);
  const { isRequiredMotivoAtendimento } = useAppSelector(atendimentosSelector);

  const [motivo, setMotivo] = useState<AtendimentoReasonInterface | null>(null);
  const [tagsSelecionadas, setTagsSelecionadas] = useState<TagInterface[]>([]);
  const [errorMotivo, setErrorMotivo] = useState(false);

  useEffect(() => {
    if (open) {
      dispatch(fetchTags());
      dispatch(fetchFinishReasonSetting());
    }
  }, [open, dispatch]);

  const handleConfirm = () => {
    if (isRequiredMotivoAtendimento && !motivo) {
      setErrorMotivo(true);
      return;
    }

    onConfirm({
      atendimentoReasonId: motivo?.id,
      tagIds: tagsSelecionadas.map(t => t.id).filter(Boolean) as string[],
    });
  };

  const handleClose = () => {
    if (!loading) {
      setMotivo(null);
      setTagsSelecionadas([]);
      setErrorMotivo(false);
      onClose();
    }
  };

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      fullWidth
      maxWidth="xs"
      className={classes.root}
    >
      <DialogHeader
        title="Finalizar atendimento"
        description={`Deseja mesmo finalizar o atendimento com ${contactName}?`}
        onClose={handleClose}
      />

      <DialogContent className={classes.content}>
        <AutocompleteAtendimentoReason
          onlyUserReasons
          label={`Motivo do atendimento${
            isRequiredMotivoAtendimento ? '' : ' (opcional)'
          }`}
          value={motivo}
          onChange={(_, newValue) => {
            setMotivo(newValue);
            if (newValue) setErrorMotivo(false);
          }}
          error={errorMotivo}
          helperText={errorMotivo ? 'Campo obrigatório' : ''}
        />

        <Autocomplete
          multiple
          disableCloseOnSelect
          options={tags}
          getOptionLabel={option => option.nome || ''}
          value={tagsSelecionadas}
          onChange={(_, newValue) => setTagsSelecionadas(newValue)}
          getOptionSelected={(option, value) => option.id === value.id}
          popupIcon={<ExpandMore />}
          size="small"
          closeText="Fechar"
          openText="Abrir"
          clearText="Limpar"
          classes={{
            listbox: classes.listbox,
            option: classes.option,
          }}
          renderTags={value =>
            value.map((option, index) => (
              <Box
                key={option.id}
                style={{
                  margin: 2,
                  marginRight: index === value.length - 1 ? 8 : 2,
                }}
              >
                <TagItem
                  tag={option}
                  onRemove={tagId =>
                    setTagsSelecionadas(prev =>
                      prev.filter(tag => tag.id !== tagId),
                    )
                  }
                />
              </Box>
            ))
          }
          renderOption={option => (
            <Box className={classes.row} key={option.id}>
              <TagItem tag={option} />
            </Box>
          )}
          renderInput={params => (
            <TextField
              {...params}
              label="Tag do atendimento (opcional)"
              variant="outlined"
              fullWidth
            />
          )}
        />
      </DialogContent>

      <DialogActions>
        <Button onClick={handleClose} className={classes.cancelButton}>
          Cancelar
        </Button>
        <Button
          onClick={handleConfirm}
          className={classes.confirmButton}
          variant="contained"
          disabled={loading}
        >
          Finalizar
        </Button>
      </DialogActions>
    </Dialog>
  );
}
